import { useState } from 'react';
import { SentraWordmark } from '@/components/brand/SentraMark';
//import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
//import { ROUTES } from '@/constants/routes';
import { useToast } from '@/hooks/use-toast';
import { AnimatedBackground } from '@/components/common/AnimatedBackground';
//import { ArrowLeft } from 'lucide-react';

export function ForgotPassword() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      await api.post('/auth/password-reset/request/', { email });
      
      toast({
        title: 'Password Reset Link Sent',
        description: 'If an account exists with this email, you will receive a password reset link',
      });
      
      setIsSubmitted(true);
    } catch (err) {
      // Even if the email doesn't exist, we don't want to reveal that information
      // Show the same message as success case
      setIsSubmitted(true);
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="min-h-screen relative flex flex-col items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      {/* Animated Background */}
      <AnimatedBackground />
      
      {/* Main Content */}
      <div className="relative z-10 flex flex-col items-center w-full max-w-md">
        {/* Forgot Password Card with integrated logo */}
        <Card className="w-full bg-white/95 backdrop-blur-sm shadow-2xl border-0 rounded-3xl overflow-hidden">
          {/* Logo section integrated into card header */}
          <CardHeader className="space-y-6 pt-8 pb-6 text-center">
            <div className="flex justify-center">
              <SentraWordmark size={44} />
            </div>
            <div className="space-y-2">
              <CardTitle className="text-3xl font-bold text-gray-800">Forgot Password</CardTitle>
              <CardDescription className="text-gray-600 text-base">
                {isSubmitted 
                  ? 'Please check your email for reset instructions' 
                  : 'Enter your email address to receive a password reset link'}
              </CardDescription>
            </div>
          </CardHeader>
          
          {!isSubmitted ? (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-5 px-8">
                {error && (
                  <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg text-sm border border-red-200 font-medium">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 font-semibold text-sm">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="name@example.com"
                    className="bg-white border-gray-300 focus:border-sidebar-accent focus:ring-sidebar-accent rounded-xl h-12 text-gray-700 placeholder:text-gray-400"
                    required
                  />
                </div>
              </CardContent>
              
              <CardFooter className="px-8 pb-8 pt-4">
                <Button
                  type="submit"
                  className="w-full h-12 text-sidebar-foreground font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 text-base hover:opacity-90 bg-sidebar hover:bg-sidebar/90"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </CardFooter>
            </form>
          ) : (
            <CardContent className="space-y-4 px-8 pb-8">
              <p className="text-center text-gray-600 text-base">
                A password reset link has been sent to your email address if an account exists.
              </p>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}